import {FormGroup} from '@angular/forms';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    AmsService,
    ApiError,
    ApiResponse,
    CompanySummaryInterface,
    CustomerProductInterface,
    EmailTemplateInputModel,
    EmailTemplateInterface,
    EmailTemplateModel,
    HttpStatusCodesEnum,
    MessagingStatusesEnum,
    PortalInterface
} from '@spa/api';

import {
    EmailTemplateFooterSelector,
    EmailTemplateHeaderSelector,
    EmailTemplateSignatureSelector,
    MessageTemplateCustomerProductIdSelector,
    MessageTemplateCustomerSelector,
    MessageTemplateStatusSelector,
    MessageTemplateTemplateNameSelector,
} from 'appAssets/selectors';
import {Helper} from 'appShared/classes';
import {PanelSettingsMessagingResolverErrorStatesEnum} from 'appEntries/panel/enums';
import {EmailTemplatesControls, EmailTemplatesControlsInterface} from 'appAssets/descriptors';
import {AutocompleteOptionInterface} from 'appShared/components';
import {BaseMessageTemplatesForm} from './panel-settings-base-messaging-templates.form';
import {AmsPermissionService} from 'appEntries/panel/services';
import {PanelSettingsMessagingMessages} from 'appAssets/messages';

export class EmailTemplatesForm extends BaseMessageTemplatesForm<EmailTemplatesControlsInterface> {
    constructor(
        protected readonly amsService: AmsService,
        private readonly amsPermissionService: AmsPermissionService,
        protected readonly companies: CompanySummaryInterface[],
        private readonly products: PortalInterface[],
        public templateModel: EmailTemplateModel = null,
        protected readonly departments: string[] = [],
    ) {
        super(amsService, companies, templateModel, departments);
        this.hasApprovePermission = amsPermissionService.hasApproveEmailTemplate();
    }

    private getEmailTemplateInputModel(customer: string, customerProduct: string): EmailTemplateInputModel {
        return new EmailTemplateInputModel(
            this.form.value[MessageTemplateTemplateNameSelector],
            this.form.value[MessageTemplateStatusSelector],
            customer,
            customerProduct,
            this.form.value[EmailTemplateFooterSelector],
            this.form.value[EmailTemplateHeaderSelector],
            this.form.value[EmailTemplateSignatureSelector]
        );
    }

    private updateEmailTemplate(): Observable<ApiResponse<ApiError[] | boolean>> {

        const customer = this.form.value[MessageTemplateCustomerSelector];
        const customerProduct = this.form.value[MessageTemplateCustomerProductIdSelector];
        const inputModel = this.getEmailTemplateInputModel(customer, customerProduct);

        return this.amsService.updateEmailTemplate(this.templateModel.id, inputModel)
            .pipe(
                map((response) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(
                            response,
                            PanelSettingsMessagingResolverErrorStatesEnum.UpdateEmailTemplateFailed
                        );
                    }

                    const emailTemplateData = response.data as EmailTemplateInterface;
                    this.templateModel = new EmailTemplateModel(emailTemplateData);

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private createEmailTemplate(): Observable<ApiResponse<ApiError[] | boolean>> {
        const customer = this.form.value[MessageTemplateCustomerSelector];
        const customerProduct = this.form.value[MessageTemplateCustomerProductIdSelector];
        const inputModel = this.getEmailTemplateInputModel(customer, customerProduct);

        return this.amsService.createEmailTemplate(inputModel)
            .pipe(
                map(response => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(
                            response,
                            PanelSettingsMessagingResolverErrorStatesEnum.CreateEmailTemplateFailed
                        );
                    }

                    const emailTemplateData = response.data as EmailTemplateInterface;
                    this.templateModel = new EmailTemplateModel(emailTemplateData);

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private prepareCustomerProductOptions(): void {
        this.controls.customerProduct.options = this.products.map((item: CustomerProductInterface): AutocompleteOptionInterface => {
            return {
                label: item.name,
                value: item.id
            };
        });
    }

    getControlValue(name: string): string | number {
        switch (name) {
            case MessageTemplateTemplateNameSelector:
                return this.templateModel.name;
            case MessageTemplateStatusSelector:
                return this.templateModel.status;
            case EmailTemplateHeaderSelector:
                return this.templateModel.header;
            case EmailTemplateFooterSelector:
                return this.templateModel.footer;
            case EmailTemplateSignatureSelector:
                return this.templateModel.signature;
        }

        return null;
    }

    initForm(): void {
        this.form = new FormGroup({});

        this.controls = EmailTemplatesControls(!this.templateModel, this.hasApprovePermission);
        if (!this.templateModel) {
            this.controls.status.value = MessagingStatusesEnum.PendingApproval;
            this.controls.status.disabled = true;
        } else {
            this.controls.status.value = this.templateModel.status;
            this.controls.status.options.map(option => {
                option.checked = option.value === this.templateModel.status;
            });

            /**
             * Validators are reset, because in edit mode name cannot be changed.
             */
            this.controls.name.resetValidators();
        }

        this.controls.company.options = this.prepareCompaniesOptions();
        this.prepareCustomerProductOptions();
        this.prepareFormValue();
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        if (!this.templateModel) {
            return this.createEmailTemplate();
        }

        return this.updateEmailTemplate();
    }
}
